/**
 * The languages of the public pages: English at the root, every other language under its own prefix with the same
 * English slugs (`/de/pricing`, `/de` for the landing page). Only the pages in LOCALIZED_PATHS exist in every
 * language; the app (/app, sign-in, the link pages), contact, help, guides and the legal pages stay English, and a
 * translated page says so where it links them. A page's language is its URL's prefix — never the browser's — so a
 * search engine, a shared link and a visitor all see the same language at the same address.
 *
 * Pure (no DOM, no path aliases): the SPA (i18n, links, head tags) and the web Worker (worker/pages.ts) import it.
 */
export const LOCALES = ["nb", "de", "es", "fr", "ru", "zh"] as const;
export type Locale = (typeof LOCALES)[number];
export type PageLocale = "en" | Locale;

/** BCP 47 tag: hreflang, `<html lang>`, number and date formatting, and the translation tables' names. */
export const LOCALE_TAG: Readonly<Record<PageLocale, string>> = { en: "en", nb: "nb", de: "de", es: "es", fr: "fr", ru: "ru", zh: "zh-Hans" };

/** Open Graph `og:locale` (language_TERRITORY). */
export const OG_LOCALE: Readonly<Record<PageLocale, string>> = { en: "en_US", nb: "nb_NO", de: "de_DE", es: "es_ES", fr: "fr_FR", ru: "ru_RU", zh: "zh_CN" };

/** Each language's name in that language, for the language links. */
export const LOCALE_NAME: Readonly<Record<PageLocale, string>> = { en: "English", nb: "Norsk bokmål", de: "Deutsch", es: "Español", fr: "Français", ru: "Русский", zh: "简体中文" };

/** The English public pages that exist in every language, in sitemap order. */
export const LOCALIZED_PATHS = [
  "/",
  "/features",
  "/pricing",
  "/wedding-checklist",
  "/wedding-budget-calculator",
  "/wedding-day-timeline",
  "/wedding-seating-chart",
  "/wedding-guest-list-template",
  "/when-to-send-wedding-invitations",
  "/wedding-countdown",
] as const;
export type LocalizedPath = (typeof LOCALIZED_PATHS)[number];

export const isLocale = (value: string): value is Locale => (LOCALES as readonly string[]).includes(value);
export const isLocalizedPath = (path: string): path is LocalizedPath => (LOCALIZED_PATHS as readonly string[]).includes(path);

/** `/de/pricing` → `{ locale: "de", path: "/pricing" }`, `/de` → `{ "de", "/" }`; any other path is English as it is. */
export function splitLocale(pathname: string): { locale: PageLocale; path: string } {
  const [, first = "", ...rest] = pathname.split("/");
  if (!isLocale(first)) return { locale: "en", path: pathname };
  const path = `/${rest.join("/")}`.replace(/\/+$/, "");
  return { locale: first, path: path === "" ? "/" : path };
}

/** The language of the page at `pathname`. */
export const pageLocale = (pathname: string): PageLocale => splitLocale(pathname).locale;

/** The current page's language in the browser; English anywhere without a `location` (tests, the Worker). */
export function currentLocale(): PageLocale {
  const here = (globalThis as { location?: { pathname: string } }).location;
  return here ? pageLocale(here.pathname) : "en";
}

/**
 * `path` (an English public path) in `locale`: `/pricing` → `/de/pricing`, `/` → `/de`. English, and every page that
 * has no translation, keeps its English address.
 */
export function localizePath(path: string, locale: PageLocale): string {
  if (locale === "en" || !isLocalizedPath(path)) return path;
  return path === "/" ? `/${locale}` : `/${locale}${path}`;
}

/** Every language version of the English page `path`, English first; only English for a page without translations. */
export function alternates(path: string): { locale: PageLocale; path: string }[] {
  if (!isLocalizedPath(path)) return [{ locale: "en", path }];
  return (["en", ...LOCALES] as PageLocale[]).map((locale) => ({ locale, path: localizePath(path, locale) }));
}
